class ProductHotspot {
	constructor(sectionEl) {
		this.section = sectionEl;
		this.imageArea = sectionEl.querySelector(".product-hotspot__image-area");
		this.svg = sectionEl.querySelector(".product-hotspot__lines");
		this.markers = sectionEl.querySelectorAll(".product-hotspot__marker");
		this.popups = sectionEl.querySelectorAll(".product-hotspot__popup");

		// Mobile overlay backdrop (shared, created once)
		this.overlay = this._getOrCreateOverlay();

		this._resizeTimer = null;
		this._boundResize = this._onResize.bind(this);

		this._init();
	}

	_init() {
		this._drawLines();
		this._bindEvents();

		window.addEventListener("resize", this._boundResize);

		// Redraw lines once the product image finishes loading
		const img = this.imageArea.querySelector(".product-hotspot__product-image");
		if (img && !img.complete) {
			img.addEventListener("load", () => this._drawLines());
		}
	}

	// ── SVG dashed lines ───────────────────────────────────────

	_drawLines() {
		if (!this.svg) return;
		this.svg.innerHTML = "";

		const areaRect = this.imageArea.getBoundingClientRect();

		this.markers.forEach((marker) => {
			const id = marker.dataset.hotspot;
			const label = this.imageArea.querySelector(`[data-label="${id}"]`);
			if (!label) return;

			const mRect = marker.getBoundingClientRect();
			const lRect = label.getBoundingClientRect();

			const x1 = mRect.left + mRect.width / 2 - areaRect.left;
			const y1 = mRect.top + mRect.height / 2 - areaRect.top;
			const x2 = lRect.left + lRect.width / 2 - areaRect.left;
			const y2 = lRect.top + lRect.height / 2 - areaRect.top;

			// Pull the label end back by 7 so the line doesn't overlap the text
			const dx = x2 - x1;
			const dy = y2 - y1;
			const dist = Math.sqrt(dx * dx + dy * dy);
			const gap = 7;
			const x2g = dist > gap ? x2 - (dx / dist) * gap : x2;
			const y2g = dist > gap ? y2 - (dy / dist) * gap : y2;

			const line = document.createElementNS(
				"http://www.w3.org/2000/svg",
				"line",
			);
			line.setAttribute("x1", x1);
			line.setAttribute("y1", y1);
			line.setAttribute("x2", x2g);
			line.setAttribute("y2", y2g);
			line.setAttribute("stroke", "#1a1a1a");
			line.setAttribute("stroke-width", "1.4");
			line.setAttribute("stroke-dasharray", "3 3");
			line.setAttribute("stroke-linecap", "round");
			this.svg.appendChild(line);
		});
	}

	// ── Events ─────────────────────────────────────────────────

	_bindEvents() {
		this.markers.forEach((marker) => {
			marker.addEventListener("click", (e) => {
				e.stopPropagation();
				this._togglePopup(marker);
			});
		});

		this.popups.forEach((popup) => {
			popup
				.querySelector(".product-hotspot__popup-close")
				?.addEventListener("click", (e) => {
					e.stopPropagation();
					this._closeAll();
				});
		});

		this.overlay.addEventListener("click", () => this._closeAll());

		document.addEventListener("click", (e) => {
			if (
				!e.target.closest(".product-hotspot__popup") &&
				!e.target.closest(".product-hotspot__marker")
			) {
				this._closeAll();
			}
		});

		document.addEventListener("keydown", (e) => {
			if (e.key === "Escape") this._closeAll();
		});
	}

	// ── Popup open / close ─────────────────────────────────────

	_togglePopup(marker) {
		const id = marker.dataset.hotspot;
		const popup = this.imageArea.querySelector(`[data-popup="${id}"]`);
		if (!popup) return;

		const isOpen = !popup.hidden;
		this._closeAll();

		if (!isOpen) {
			this._openPopup(popup, marker);
		}
	}

	_openPopup(popup, marker) {
		const isMobile = window.innerWidth < 750;

		if (isMobile) {
			// Center fixed on viewport via CSS — just show it
			popup.style.removeProperty("left");
			popup.style.removeProperty("top");
			popup.hidden = false;
			this.overlay.classList.add("is-visible");
		} else {
			this._positionPopup(popup, marker);
			popup.hidden = false;
		}

		marker.setAttribute("aria-expanded", "true");
	}

	_positionPopup(popup, marker) {
		const areaRect = this.imageArea.getBoundingClientRect();
		const mRect = marker.getBoundingClientRect();

		const markerLeftPct =
			((mRect.left + mRect.width / 2 - areaRect.left) / areaRect.width) * 100;
		const markerTopPct = ((mRect.top - areaRect.top) / areaRect.height) * 100;

		const POPUP_W_PX = 260;
		const popupWidthPct = (POPUP_W_PX / areaRect.width) * 100;

		// Default: popup appears above and slightly left of the marker
		let leftPct = markerLeftPct - 2;
		let topPct = markerTopPct - 32;

		// Clamp right edge
		if (leftPct + popupWidthPct > 96) {
			leftPct = Math.max(2, markerLeftPct - popupWidthPct - 2);
		}

		// If too close to top, flip below the marker
		if (topPct < 2) {
			topPct = markerTopPct + 10;
		}

		popup.style.left = `${Math.max(1, leftPct)}%`;
		popup.style.top = `${Math.max(1, topPct)}%`;
	}

	_closeAll() {
		this.popups.forEach((p) => {
			p.hidden = true;
		});
		this.markers.forEach((m) => m.setAttribute("aria-expanded", "false"));
		this.overlay.classList.remove("is-visible");
	}

	// ── Resize ─────────────────────────────────────────────────

	_onResize() {
		clearTimeout(this._resizeTimer);
		this._resizeTimer = setTimeout(() => {
			this._drawLines();
			this._closeAll();
		}, 120);
	}

	// ── Mobile overlay ─────────────────────────────────────────

	_getOrCreateOverlay() {
		let overlay = document.querySelector(".product-hotspot-overlay");
		if (!overlay) {
			overlay = document.createElement("div");
			overlay.className = "product-hotspot-overlay";
			document.body.appendChild(overlay);
		}
		return overlay;
	}

	// ── Cleanup (for theme editor re-loads) ────────────────────

	destroy() {
		window.removeEventListener("resize", this._boundResize);
	}
}

// ── Init ───────────────────────────────────────────────────

function initProductHotspots(scope = document) {
	scope.querySelectorAll(".product-hotspot").forEach((el) => {
		if (!el._hotspotInstance) {
			el._hotspotInstance = new ProductHotspot(el);
		}
	});
}

initProductHotspots();

// Shopify theme editor support
if (window.Shopify?.designMode) {
	document.addEventListener("shopify:section:load", (e) => {
		initProductHotspots(e.target);
	});

	document.addEventListener("shopify:section:unload", (e) => {
		const el = e.target.querySelector(".product-hotspot");
		if (el?._hotspotInstance) {
			el._hotspotInstance.destroy();
			delete el._hotspotInstance;
		}
	});

	// Redraw lines when a block is selected/deselected in the editor
	document.addEventListener("shopify:block:select", (e) => {
		const section = e.target.closest(".product-hotspot");
		section?._hotspotInstance?._drawLines();
	});
}
